import { isSuperAdmin, normalizeName, normalizeRole } from './access-helpers'

export { normalizeName }

export interface AccessRequirements {
  match?: MatchMode
  permissions?: readonly string[]
  roles?: readonly string[]
}

export interface AccessResult {
  allowed: boolean
  hasPermission: boolean
  hasRole: boolean
  isAuthenticated: boolean
  missingPermissions: string[]
  missingRoles: string[]
}

export interface AccessSubject {
  directPermissions?: null | readonly string[]
  permissions?: null | readonly string[]
  role?: NamedRole | null | string
  roles?: null | readonly (NamedRole | string)[]
}

export type MatchMode = 'all' | 'any'

export interface NamedRole {
  name: string
}

export const hasPermission = (subject: AccessSubject | null, permission: string): boolean => {
  if (isSuperAdmin(subject)) {
    return true
  }

  return hasExplicitPermission(subject, permission)
}

export const hasExplicitPermission = (
  subject: AccessSubject | null,
  permission: string,
): boolean => {
  const normalized = normalizeName(permission)
  return subject?.permissions?.some(value => normalizeName(value) === normalized) ?? false
}

export const hasRole = (subject: AccessSubject | null, role: string): boolean => {
  const normalized = normalizeName(role)
  const fromRoles = subject?.roles?.some(value => normalizeRole(value) === normalized) ?? false
  const fromRole = subject?.role ? normalizeRole(subject.role) === normalized : false

  return fromRoles || fromRole
}

export const checkAccess = (
  subject: AccessSubject | null,
  { match = 'any', permissions = [], roles = [] }: AccessRequirements,
): AccessResult => {
  const isAuthenticated = !!subject
  const requiredPermissions = permissions.map(normalizeName)
  const requiredRoles = roles.map(normalizeName)

  const missingPermissions = requiredPermissions.filter(
    permission => !hasPermission(subject, permission),
  )
  const missingRoles = requiredRoles.filter(role => !hasRole(subject, role))
  const hasRequiredPermission = requiredPermissions.length > 0 && missingPermissions.length === 0
  const hasRequiredRole = requiredRoles.length > 0 && missingRoles.length === 0

  if (match === 'any') {
    const hasNoRequirements = requiredPermissions.length === 0 && requiredRoles.length === 0

    return {
      allowed: isAuthenticated && (hasNoRequirements || hasRequiredPermission || hasRequiredRole),
      hasPermission: hasRequiredPermission,
      hasRole: hasRequiredRole,
      isAuthenticated,
      missingPermissions,
      missingRoles,
    }
  }

  const permissionOk = requiredPermissions.length === 0 || hasRequiredPermission
  const roleOk = requiredRoles.length === 0 || hasRequiredRole

  return {
    allowed: isAuthenticated && permissionOk && roleOk,
    hasPermission: hasRequiredPermission,
    hasRole: hasRequiredRole,
    isAuthenticated,
    missingPermissions,
    missingRoles,
  }
}
